import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import { Stack } from '@atlaskit/primitives/compiled/stack';

import {
	ANALYTICS_SOURCE,
	PEOPLE_AND_AGENTS_SHOW_MORE_ACTION_SUBJECT_ID,
} from '../../analytics.ts';
import type { RankedPagePersonOrAgent } from '../../utils/rankPagePeopleAndAgents.ts';
import { useExpandedState } from '../hooks/useExpandedState.ts';
import { PressableGroup } from '../pressable-group/PressableGroup.tsx';
import { SectionHeader } from '../pressable-group/SectionHeader.tsx';
import { ShowMoreRow } from '../pressable-group/ShowMoreRow.tsx';

import { useTwgAnalyticsEvents } from '../../useTwgAnalyticsEvents.ts';
import { PersonListRow } from './PersonListRow.tsx';
import { SectionEmptyState } from './SectionEmptyState.tsx';
import type { SectionExpansionProps } from './useSectionExpansion.ts';

/**
 * How many people the collapsed section lists. The rest are one press away, up to the
 * `MAX_LISTED_PAGE_PEOPLE` `useConnectionSectionsData` asks `usePagePeople` for.
 */
export const COLLAPSED_PAGE_PEOPLE = 3;

const i18n = defineMessages({
	heading: {
		id: 'byline-connections.preview-panel.section.people-and-agents',
		defaultMessage: 'People & Agents',
		description:
			'Heading of the Teamwork Graph section listing the people connected to this page, such as the people who created or contributed to it. The section shows the number of people in a badge beside the heading.',
	},
	empty: {
		id: 'byline-connections.preview-panel.section.people-and-agents.empty',
		defaultMessage:
			'There are no contributors yet. Relationships are mapped automatically as your team works together on this page. Start a conversation or share this page to strengthen your team’s graph.',
		description:
			'Shown under the "People & Agents" heading of the Teamwork Graph panel, in place of the list of people, when the graph connects nobody to the current page yet.',
	},
});

export type PeopleAndAgentsSectionProps = SectionExpansionProps & {
	/**
	 * Already ranked by `usePagePeople` and capped there to how many this section can list at
	 * most; this section does not re-rank. It applies only the collapsed cap below.
	 */
	people: readonly RankedPagePersonOrAgent[];
	loading: boolean;
	/** A failed source must not cause an empty-state claim, but available rows still render. */
	hasError: boolean;
};

/**
 * People, agent contributors and viewers connected to this page, on both the panel and modal.
 * Available rows render independently of the other source's loading/error state. A successfully
 * settled empty list keeps the heading with a 0 badge and explains itself through `SectionEmptyState`.
 *
 * Lists the three most relevant people and expands to the rest on a press. Expansion follows the
 * same controlled/uncontrolled split as `ConnectionsByLineSectionShell`, so the modal's
 * `useSectionExpansion` controller holds it across the swap to Rovo exactly as it does for the
 * sections above, and the panel — which passes no controller — owns it locally.
 *
 * Agent contributors and viewers share the same relationship ranking and list cap as people.
 */
export const PeopleAndAgentsSection = ({
	people,
	loading,
	hasError,
	isExpanded: controlledExpanded,
	onToggleExpanded,
}: PeopleAndAgentsSectionProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useTwgAnalyticsEvents();
	const [isExpanded, toggleExpanded] = useExpandedState(controlledExpanded, onToggleExpanded);

	const onToggle = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: PEOPLE_AND_AGENTS_SHOW_MORE_ACTION_SUBJECT_ID,
				source: ANALYTICS_SOURCE,
				// The direction of this press, not the state it was in — `true` is an expand.
				attributes: { isExpanding: !isExpanded },
			},
		}).fire();

		toggleExpanded();
	}, [createAnalyticsEvent, isExpanded, toggleExpanded]);

	// Nothing to show while discovery is in flight, and nothing to explain when it failed: an
	// error is not an empty graph, so the section stays out rather than claiming no one is here.
	if (people.length === 0 && (loading || hasError)) {
		return null;
	}

	const hasHiddenPeople = people.length > COLLAPSED_PAGE_PEOPLE;
	const listedPeople = isExpanded ? people : people.slice(0, COLLAPSED_PAGE_PEOPLE);

	return (
		<Stack space="space.100" testId="teamwork-graph-people-and-agents">
			{/* The count is everyone the section can list, expanded or not, so it does not move
			    under the reader as they expand it. */}
			<SectionHeader
				icon={<PeopleGroupIcon label="" color="currentColor" />}
				label={formatMessage(i18n.heading)}
				count={people.length}
			/>
			{people.length === 0 ? (
				<SectionEmptyState
					message={formatMessage(i18n.empty)}
					testId="teamwork-graph-people-and-agents-empty"
				/>
			) : (
				<PressableGroup testId="teamwork-graph-people-and-agents-group">
					{listedPeople.map((personOrAgent) => (
						<PersonListRow key={personOrAgent.ari} personOrAgent={personOrAgent} />
					))}
					{hasHiddenPeople ? (
						<ShowMoreRow
							isExpanded={isExpanded}
							onClick={onToggle}
							testId="teamwork-graph-people-and-agents-show-more"
						/>
					) : null}
				</PressableGroup>
			)}
		</Stack>
	);
};
